import React, { useEffect, useState, useCallback, useRef } from "react";
import PageHelp from "@/components/PageHelp";
import OfflineReadyBadge, { ReloadButton, useOfflineReadyProjects, OfflineMissingHint, OfflineCounts } from "@/components/OfflineReadyBadge";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  Building2, Plus, Layers, FileStack, ChevronRight, Upload, FileDown, Loader2, Trash2, ArrowLeft, MapPin, Ruler, CalendarDays, FolderOpen, AlertTriangle, Ticket, Pencil,
} from "lucide-react";
import ProjectFormDialog from "@/components/ProjectFormDialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { PROJECT_STATUS, PROJECT_STATUS_STYLE, DEFECT_STATUS_OPTIONS, PRIORITIES } from "@/lib/constants";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import QuantitiesPanel from "@/components/QuantitiesPanel";
import DailyReportsPanel from "@/components/DailyReportsPanel";
import DocumentsPanel from "@/components/DocumentsPanel";
import ReportDialog from "@/components/ReportDialog";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function IconTextDialog({ trigger, title, label, placeholder, onSubmit, extra }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await onSubmit(val); setOpen(false); setVal(""); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>{label}</Label><Input autoFocus required value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} className="mt-1.5" data-testid="dialog-text-input" /></div>
          {extra}
          <DialogFooter><Button type="submit" disabled={busy} data-testid="dialog-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Speichern"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const DEF_ST = {
  offen: "bg-slate-100 text-slate-700 border-slate-200",
  in_bearbeitung: "bg-sky-50 text-sky-700 border-sky-200",
  behoben: "bg-emerald-50 text-emerald-700 border-emerald-200",
  erledigt: "bg-emerald-50 text-emerald-700 border-emerald-200",
  abgenommen: "bg-emerald-50 text-emerald-700 border-emerald-200",
  geschlossen: "bg-slate-100 text-slate-700 border-slate-200",
};
const defStClass = (s) => DEF_ST[s] || "bg-slate-100 text-slate-700 border-slate-200";
const DEF_CLOSED = ["erledigt", "abgenommen", "geschlossen", "behoben"];
const EMPTY_DEF = { bezeichnung: "", location: "", company: "", priority: "normal", due_date: "", status: "offen" };

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = can(user, "plans:manage");
  const [project, setProject] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState({});
  const [plans, setPlans] = useState({});
  const [expanded, setExpanded] = useState({});
  const [reportOpen, setReportOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [defectsOpen, setDefectsOpen] = useState(false);
  const [defects, setDefects] = useState(null);
  const [creatingDef, setCreatingDef] = useState(false);
  const [newDef, setNewDef] = useState(EMPTY_DEF);
  const [savingDef, setSavingDef] = useState(false);
  const loadDefects = useCallback(() => api.get(`/search?kind=defects&project_id=${projectId}`).then((r) => setDefects(r.data.defects || [])).catch(() => setDefects([])), [projectId]);
  const openDefects = () => { setDefectsOpen(true); setDefects(null); setCreatingDef(false); setNewDef(EMPTY_DEF); loadDefects(); };
  const submitDefect = async (e) => {
    e.preventDefault();
    if (!newDef.bezeichnung.trim()) return toast.error("Bezeichnung ist erforderlich");
    setSavingDef(true);
    try {
      await api.post(`/projects/${projectId}/defects`, newDef);
      toast.success("Mangel angelegt");
      setNewDef(EMPTY_DEF); setCreatingDef(false); loadDefects(); loadProject();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingDef(false); }
  };
  const setDef = (k, v) => setNewDef((f) => ({ ...f, [k]: v }));
  const [logoUrl, setLogoUrl] = useState(null);
  const offlineReady = useOfflineReadyProjects();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "struktur";

  const loadLogo = useCallback(() => api.get(`/projects/${projectId}/logo`, { responseType: "blob" }).then((r) => setLogoUrl(URL.createObjectURL(r.data))).catch(() => setLogoUrl(null)), [projectId]);
  const uploadLogo = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/projects/${projectId}/logo`, fd); toast.success("Logo gespeichert"); loadLogo(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Offline fallback: if the detail isn't cached yet, use the entry from the cached project list.
  const loadProject = useCallback(() => api.get(`/projects/${projectId}`).then((r) => setProject(r.data))
    .catch(async (e) => {
      const r = await api.get("/projects").catch(() => null);
      const hit = (r?.data || []).find?.((p) => p.id === projectId);
      if (hit) setProject(hit); else throw e;
    }), [projectId]);
  const loadBuildings = useCallback(() => api.get(`/projects/${projectId}/buildings`).then((r) => setBuildings(r.data)).catch(() => setBuildings([])), [projectId]);

  useEffect(() => { loadProject(); loadBuildings(); loadLogo(); }, [loadProject, loadBuildings, loadLogo]);

  // On open: auto-expand buildings/floors whose plans have defects, so plans with Mängel show first.
  const autoExpanded = useRef(false);
  useEffect(() => {
    if (autoExpanded.current || buildings.length === 0) return;
    autoExpanded.current = true;
    (async () => {
      const nf = {}, np = {}, exp = {};
      for (const b of buildings) {
        try {
          const fr = await api.get(`/buildings/${b.id}/floors`);
          nf[b.id] = fr.data;
          for (const f of fr.data) {
            const pr = await api.get(`/floors/${f.id}/plans`);
            np[f.id] = pr.data;
            if ((pr.data || []).some((pl) => (pl.defect_count || 0) > 0)) {
              exp[`b-${b.id}`] = true;
              exp[`f-${f.id}`] = true;
            }
          }
        } catch (_) {}
      }
      setFloors((p) => ({ ...p, ...nf }));
      setPlans((p) => ({ ...p, ...np }));
      if (Object.keys(exp).length) setExpanded((p) => ({ ...p, ...exp }));
    })();
  }, [buildings]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFloors = async (bId) => { const r = await api.get(`/buildings/${bId}/floors`); setFloors((p) => ({ ...p, [bId]: r.data })); };
  const loadPlans = async (fId) => { const r = await api.get(`/floors/${fId}/plans`); setPlans((p) => ({ ...p, [fId]: r.data })); };

  const toggle = (key, loader) => {
    setExpanded((p) => ({ ...p, [key]: !p[key] }));
    if (!expanded[key] && loader) loader();
  };

  const addBuilding = async (name) => { await api.post(`/projects/${projectId}/buildings`, { name }); toast.success("Gebäude hinzugefügt"); loadBuildings(); };
  const addFloor = async (bId, name) => { await api.post(`/buildings/${bId}/floors`, { name, level: (floors[bId]?.length || 0) }); toast.success("Geschoss hinzugefügt"); loadFloors(bId); };

  const uploadPlan = async (fId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", file.name);
    try {
      await api.post(`/floors/${fId}/plans`, fd);
      toast.success("Bauplan hochgeladen");
      await loadPlans(fId);
      setExpanded((p) => ({ ...p, [`f-${fId}`]: true }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delItem = async (url, reloader) => { await api.delete(url); toast.success("Gelöscht"); reloader(); };

  if (!project) return <Skeleton className="h-64 rounded-xl" />;
  const st = PROJECT_STATUS.find((s) => s.value === project.status);

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/projects")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary" data-testid="back-to-projects"><ArrowLeft className="h-4 w-4" />Alle Projekte</button>

      <Card className="p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-4">
            <label className={`shrink-0 ${isAdmin ? "cursor-pointer" : "pointer-events-none"}`} title="Projektlogo für PDF">
              {isAdmin && <input type="file" accept="image/*" className="hidden" data-testid="logo-upload-input" onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} />}
              {logoUrl ? <img src={logoUrl} alt="Logo" className="h-14 w-20 object-contain rounded-lg border border-slate-200 bg-white" /> : <div className="h-14 w-20 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center text-[10px] text-slate-400 text-center px-1 hover:border-primary/50">Logo hochladen</div>}
            </label>
            <div>
              <div className="flex items-center gap-3">
                {(project.field_visibility?.number ?? true) && <div className="text-xs font-mono-tech text-slate-400">{project.number || "—"}</div>}
                <Badge variant="outline" className={PROJECT_STATUS_STYLE[project.status]}>{st?.label}</Badge>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <h1 className="text-3xl font-head font-extrabold tracking-tight text-slate-900">{project.name}</h1>
                <PageHelp id="projekt-detail" title="Projekt-Detail" text="Hier pflegen Sie die Struktur des Projekts: Gebäude, Geschosse und Baupläne. Über „Dokumentation erstellen“ erzeugen Sie das PDF." />
              </div>
              <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-2"><MapPin className="h-4 w-4" />{project.address || "Keine Adresse"}</div>
            </div>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <Button variant="outline" onClick={() => setEditOpen(true)} className="h-11" data-testid="project-edit-btn">
                <Pencil className="h-4 w-4 mr-2" />Bearbeiten
              </Button>
            )}
            <Button variant="outline" onClick={openDefects} className="h-11" data-testid="project-defects-btn">
              <AlertTriangle className="h-4 w-4 mr-2" />Mängel
            </Button>
            <Button onClick={() => setReportOpen(true)} className="h-11" data-testid="export-pdf-btn">
              <FileDown className="h-4 w-4 mr-2" />Dokumentation erstellen
            </Button>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="project-detail-offline-row">
            {offlineReady[projectId] ? <OfflineReadyBadge projectId={projectId} ready={offlineReady} testid="project-detail-offline-ready" />
              : <OfflineCounts projectId={projectId} testid="project-detail-offline-counts" />}
            <ReloadButton projectId={projectId} testid="project-detail-offline-reload" />
          </div>
          <OfflineMissingHint projectId={projectId} />
          </div>
          <ReportDialog projectId={projectId} projectName={project.name} open={reportOpen} onClose={() => setReportOpen(false)} />
          {isAdmin && <ProjectFormDialog open={editOpen} onOpenChange={setEditOpen} project={project} onSaved={(d) => (d && d.id ? setProject(d) : loadProject())} />}
          <Dialog open={defectsOpen} onOpenChange={setDefectsOpen}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="project-defects-dialog">
              <DialogHeader>
                <DialogTitle className="font-head flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-orange-500" />Mängel · {project.name}</DialogTitle>
              </DialogHeader>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-slate-500">Alle Mängel dieses Projekts.</p>
                {!creatingDef && can(user, "defect:write") && (
                  <Button size="sm" variant="outline" onClick={() => setCreatingDef(true)} data-testid="new-project-defect-btn">
                    <Plus className="h-4 w-4 mr-1.5" />Neuer Mangel
                  </Button>
                )}
              </div>
              {creatingDef && (
                <form onSubmit={submitDefect} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="new-project-defect-form">
                  <div>
                    <Label className="text-xs">Bezeichnung *</Label>
                    <Input autoFocus value={newDef.bezeichnung} onChange={(e) => setDef("bezeichnung", e.target.value)} placeholder="z. B. Riss in Wand EG" className="mt-1" data-testid="defect-bezeichnung" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Ort / Lage</Label><Input value={newDef.location} onChange={(e) => setDef("location", e.target.value)} className="mt-1" data-testid="defect-location" /></div>
                    <div><Label className="text-xs">Firma</Label><Input value={newDef.company} onChange={(e) => setDef("company", e.target.value)} className="mt-1" data-testid="defect-company" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Priorität</Label>
                      <Select value={newDef.priority} onValueChange={(v) => setDef("priority", v)}>
                        <SelectTrigger className="mt-1 h-9" data-testid="defect-priority"><SelectValue /></SelectTrigger>
                        <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Status</Label>
                      <Select value={newDef.status} onValueChange={(v) => setDef("status", v)}>
                        <SelectTrigger className="mt-1 h-9" data-testid="defect-status"><SelectValue /></SelectTrigger>
                        <SelectContent>{DEFECT_STATUS_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Frist</Label><Input type="date" value={newDef.due_date} onChange={(e) => setDef("due_date", e.target.value)} className="mt-1 h-9" data-testid="defect-due-date" /></div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setCreatingDef(false); setNewDef(EMPTY_DEF); }} data-testid="defect-cancel-btn">Abbrechen</Button>
                    <Button type="submit" size="sm" disabled={savingDef} data-testid="defect-save-btn">{savingDef ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mangel speichern"}</Button>
                  </div>
                </form>
              )}
              <div className="overflow-y-auto -mx-1 px-1">
                {defects === null ? (
                  <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                ) : defects.length === 0 ? (
                  <p className="py-12 text-center text-slate-400" data-testid="project-defects-empty">Keine Mängel in diesem Projekt erfasst.</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {defects.map((d) => (
                      <button key={d.id} data-testid={`project-defect-${d.id}`}
                        onClick={() => d.plan_id && navigate(`/plans/${d.plan_id}?position=${d.position_id}`)}
                        className="w-full text-left flex items-center gap-3 py-3 px-2 rounded-lg hover:bg-slate-50">
                        <span className="font-mono-tech text-xs font-bold text-slate-500 shrink-0 w-14">{d.number}</span>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-900 truncate">{d.bezeichnung}</div>
                          <div className="text-xs text-slate-500 truncate">{[d.position_number, d.floor_name, d.company].filter(Boolean).join(" · ") || "—"}</div>
                        </div>
                        {d.due_date && <span className={`text-xs shrink-0 ${d.overdue ? "text-red-600 font-semibold" : "text-slate-400"}`}>{d.due_date}</span>}
                        <Badge variant="outline" className={`shrink-0 ${defStClass(d.status)}`}>{(d.status || "").replace("_", " ")}</Badge>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {defects && defects.length > 0 && (
                <div className="pt-2 border-t border-slate-100 text-xs text-slate-500" data-testid="project-defects-summary">
                  {defects.length} Mängel · offen: {defects.filter((d) => !DEF_CLOSED.includes(d.status)).length} · erledigt: {defects.filter((d) => DEF_CLOSED.includes(d.status)).length}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6 text-sm">
          {[["Bauherr", project.client, "client"], ["Auftragnehmer", project.contractor, "contractor"], ["Architekt", project.architect, "architect"], ["Bauleiter", project.site_manager, "site_manager"], ["Start", project.start_date, "start_date"], ["Ende", project.end_date, "end_date"]].filter(([, , fk]) => (project.field_visibility?.[fk] ?? true)).map(([k, v]) => (
            <div key={k}><div className="text-xs text-slate-400 uppercase tracking-wide">{k}</div><div className="text-slate-800 font-medium">{v || "—"}</div></div>
          ))}
          <div className="lg:col-span-2 sm:col-span-2">
            <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">{project.progress_mode === "maengel" ? "Mangel – noch zu Erledigen Fortschritt" : "Fortschritt"}</div>
            <div className="flex items-center gap-3"><Progress value={project.progress} className="h-2 flex-1" /><span className="font-mono-tech text-primary font-bold">{project.progress}%</span></div>
          </div>
        </div>
        {project.description && <p className="mt-4 text-sm text-slate-600 border-t border-slate-100 pt-4">{project.description}</p>}
      </Card>

      <Tabs value={tab} onValueChange={(v) => setSearchParams({ tab: v })}>
        <TabsList className="grid grid-cols-4 w-full sm:w-auto sm:inline-grid">
          <TabsTrigger value="struktur" data-testid="tab-struktur"><Building2 className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Gebäudestruktur</span></TabsTrigger>
          <TabsTrigger value="mengen" data-testid="tab-project-mengen"><Ruler className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Mengenberichte</span></TabsTrigger>
          <TabsTrigger value="tagesberichte" data-testid="tab-project-tagesberichte"><CalendarDays className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Tagesberichte</span></TabsTrigger>
          <TabsTrigger value="dokumente" data-testid="tab-project-dokumente"><FolderOpen className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Dokumente</span></TabsTrigger>
        </TabsList>
        <TabsContent value="mengen"><QuantitiesPanel projectId={projectId} /></TabsContent>
        <TabsContent value="tagesberichte"><DailyReportsPanel projectId={projectId} /></TabsContent>
        <TabsContent value="dokumente"><DocumentsPanel projectId={projectId} /></TabsContent>
        <TabsContent value="struktur" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-head font-bold flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" />Gebäudestruktur</h2>
        {isAdmin && <IconTextDialog title="Neues Gebäude" label="Gebäudename" placeholder="z.B. Haus A, Tiefgarage"
          onSubmit={addBuilding} trigger={<Button variant="outline" size="sm" data-testid="add-building-btn"><Plus className="h-4 w-4 mr-1.5" />Gebäude</Button>} />}
      </div>

      {buildings.length === 0 && <Card className="p-8 text-center text-slate-400"><Building2 className="h-10 w-10 mx-auto mb-2 text-slate-300" />{isAdmin ? "Fügen Sie das erste Gebäude hinzu." : "Noch keine Gebäude angelegt."}</Card>}

      <div className="space-y-3">
        {buildings.map((b) => (
          <Card key={b.id} className="overflow-hidden">
            <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-50" onClick={() => toggle(`b-${b.id}`, () => loadFloors(b.id))} data-testid={`building-${b.id}`}>
              <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${expanded[`b-${b.id}`] ? "rotate-90" : ""}`} />
              <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center"><Building2 className="h-4 w-4 text-primary" /></div>
              <span className="font-semibold text-slate-900 flex-1">{b.name}</span>
              <div onClick={(e) => e.stopPropagation()}>
                {isAdmin && <IconTextDialog title={`Geschoss zu ${b.name}`} label="Geschossname" placeholder="z.B. EG, OG 1, UG"
                  onSubmit={(name) => addFloor(b.id, name)} trigger={<Button variant="ghost" size="sm" data-testid={`add-floor-btn-${b.id}`}><Plus className="h-3.5 w-3.5 mr-1" />Geschoss</Button>} />}
              </div>
              {isAdmin && <DeleteBtn onConfirm={() => delItem(`/buildings/${b.id}`, loadBuildings)} testid={`delete-building-${b.id}`} />}
            </div>
            {expanded[`b-${b.id}`] && (
              <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-2 space-y-2">
                {(floors[b.id] || []).length === 0 && <p className="text-sm text-slate-400 py-2 pl-8">Noch keine Geschosse.</p>}
                {(floors[b.id] || []).map((f) => (
                  <div key={f.id} className="rounded-lg bg-white border border-slate-100">
                    <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => toggle(`f-${f.id}`, () => loadPlans(f.id))} data-testid={`floor-${f.id}`}>
                      <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${expanded[`f-${f.id}`] ? "rotate-90" : ""}`} />
                      <Layers className="h-4 w-4 text-sky-600" />
                      <span className="font-medium text-slate-800 flex-1">{f.name}</span>
                      <div onClick={(e) => e.stopPropagation()}>
                        {isAdmin && <label className="cursor-pointer">
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" data-testid={`upload-plan-input-${f.id}`}
                            onChange={(e) => e.target.files[0] && uploadPlan(f.id, e.target.files[0])} />
                          <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-primary/10 text-primary hover:bg-primary/20"><Upload className="h-3.5 w-3.5" />Bauplan</span>
                        </label>}
                      </div>
                      {isAdmin && <DeleteBtn onConfirm={() => delItem(`/floors/${f.id}`, () => loadFloors(b.id))} testid={`delete-floor-${f.id}`} />}
                    </div>
                    {expanded[`f-${f.id}`] && (
                      <div className="border-t border-slate-100 px-3 py-2 space-y-1.5">
                        {(plans[f.id] || []).length === 0 && <p className="text-sm text-slate-400 py-1 pl-7">Noch keine Baupläne. Laden Sie PDF/Bild hoch.</p>}
                        {(plans[f.id] || []).map((pl) => (
                          <div key={pl.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 pl-2 sm:pl-7 pr-2 rounded-md hover:bg-slate-50">
                            <Link to={`/plans/${pl.id}`} className="flex items-center gap-2 basis-full sm:basis-auto sm:flex-1 min-w-0 py-1 text-sm text-slate-700 hover:text-primary font-medium" data-testid={`open-plan-${pl.id}`}>
                              <FileStack className="h-4 w-4 text-emerald-600 shrink-0" /><span className="truncate">{pl.name}</span>
                            </Link>
                            {pl.defect_count > 0 && (
                              <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200" data-testid={`plan-defects-${pl.id}`}>
                                <AlertTriangle className="h-3 w-3 mr-1" />{pl.defect_count} {pl.defect_count === 1 ? "Mangel" : "Mängel"}{pl.open_defect_count > 0 ? ` · ${pl.open_defect_count} offen` : ""}
                              </Badge>
                            )}
                            {pl.ticket_count > 0 && (
                              <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200" data-testid={`plan-tickets-${pl.id}`}>
                                <Ticket className="h-3 w-3 mr-1" />{pl.ticket_count} {pl.ticket_count === 1 ? "Ticket" : "Tickets"}{pl.open_ticket_count > 0 ? ` · ${pl.open_ticket_count} offen` : ""}
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-[10px] uppercase">{(pl.content_type || "").includes("pdf") ? "PDF" : "Bild"}</Badge>
                            {isAdmin && <DeleteBtn onConfirm={() => delItem(`/plans/${pl.id}`, () => loadPlans(f.id))} testid={`delete-plan-${pl.id}`} />}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DeleteBtn({ onConfirm, testid }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button onClick={(e) => e.stopPropagation()} className="p-1.5 rounded-md hover:bg-red-50 text-slate-300 hover:text-red-500" data-testid={testid}><Trash2 className="h-3.5 w-3.5" /></button>
      </AlertDialogTrigger>
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader><AlertDialogTitle>Wirklich löschen?</AlertDialogTitle><AlertDialogDescription>Untergeordnete Elemente werden ebenfalls gelöscht.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Abbrechen</AlertDialogCancel><AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700">Löschen</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
